import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import FilterValueRow from './FilterValueRow';
import { updateFilterTypeLabel, editFilterValue, addFilterValue, deleteFilterValue } from './store/assetLibraryFilter.actions';
import { Filter, FilterValue } from './store/assetLibraryFilter.types';
import { AssetLibraryDispatch } from './store/assetLibraryFilter.types';
import { BombiixSidePanel } from '../../../Components/BombiixSidePanel';
import { BombiixModal } from '../../../Components/BombiixModal';
import { NewInput } from '../../../Components/PIM/common';

interface FilterPanelProps {
  filter: Filter;
  onClose: () => void;
  masterAddEditAccess: boolean;
  masterDeleteAccess: boolean;
}

interface ExistingValue {
  _id: string;
  label: string;
  originalLabel: string;
}

interface NewValue {
  tempId: string;
  label: string;
}

interface LocalState {
  title: string;
  values: ExistingValue[];
  newValues: NewValue[];
}

type ErrorMap = Record<string, string | undefined>;

/**
 * Error-map entry for a field left blank. The field is still flagged invalid — and Save is
 * still blocked — but an empty message renders no text under the row, so the obvious
 * "must not be empty" state is shown by the red field alone rather than spelled out.
 */
const BLANK_FIELD_ERROR = '';

/** Character limit for every filter's option values, Image Type included. */
const VALUE_MAX_LENGTH = 20;

/** Character limit for a filter's name. */
const FILTER_NAME_MAX_LENGTH = 30;

const computeDuplicateIds = (values: ExistingValue[], newValues: NewValue[]): Set<string> => {
  const idsByKey = new Map<string, string[]>();
  const register = (id: string, label: string): void => {
    const key = label.trim().toLowerCase();
    if (!key) return;
    idsByKey.set(key, [...(idsByKey.get(key) || []), id]);
  };
  values.forEach((v) => register(v._id, v.label));
  newValues.forEach((v) => register(v.tempId, v.label));

  const duplicateIds = new Set<string>();
  idsByKey.forEach((ids) => {
    if (ids.length > 1) ids.forEach((id) => duplicateIds.add(id));
  });
  return duplicateIds;
};

const buildLocalState = (filter: Filter): LocalState => ({
  title: filter.label,
  values: (filter.values || []).map((v) => ({
    _id: v._id,
    label: v.label,
    originalLabel: v.label,
  })),
  newValues: [],
});

function FilterPanel({ filter, onClose, masterAddEditAccess, masterDeleteAccess }: FilterPanelProps): JSX.Element {
  const dispatch = useDispatch<AssetLibraryDispatch>();
  const { t } = useTranslation('systemMaster');

  const [state, setState] = useState<LocalState>(() => buildLocalState(filter));
  const [errors, setErrors] = useState<ErrorMap>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState<boolean>(false);
  const valueMaxLength = VALUE_MAX_LENGTH;

  useEffect(() => {
    setState(buildLocalState(filter));
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter._id]);

  const isDirty = useMemo<boolean>(() => {
    if (state.title.trim() !== filter.label) return true;
    if (state.newValues.length > 0) return true;
    if (state.values.some((v) => v.label !== v.originalLabel)) return true;
    return false;
  }, [state, filter.label]);

  const validate = (): boolean => {
    const nextErrors: ErrorMap = {};
    const trimmedTitle = state.title.trim();
    if (!trimmedTitle) nextErrors['title'] = BLANK_FIELD_ERROR;
    else if (trimmedTitle.length > FILTER_NAME_MAX_LENGTH) nextErrors['title'] = t('ASSET_LIBRARY.LABEL_TOO_LONG');

    const duplicateIds = computeDuplicateIds(state.values, state.newValues);

    state.values.forEach((v) => {
      const trimmed = v.label.trim();
      if (!trimmed) nextErrors[v._id] = BLANK_FIELD_ERROR;
      else if (trimmed.length > valueMaxLength) nextErrors[v._id] = t('ASSET_LIBRARY.VALUE_TOO_LONG', { count: valueMaxLength });
      else if (duplicateIds.has(v._id)) nextErrors[v._id] = t('ASSET_LIBRARY.DUPLICATE_VALUE');
    });

    state.newValues.forEach((v) => {
      const trimmed = v.label.trim();
      if (!trimmed) nextErrors[v.tempId] = BLANK_FIELD_ERROR;
      else if (trimmed.length > valueMaxLength) nextErrors[v.tempId] = t('ASSET_LIBRARY.VALUE_TOO_LONG', { count: valueMaxLength });
      else if (duplicateIds.has(v.tempId)) nextErrors[v.tempId] = t('ASSET_LIBRARY.DUPLICATE_VALUE');
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (): Promise<void> => {
    if (!validate()) return;
    setIsSaving(true);

    const trimmedTitle = state.title.trim();

    if (trimmedTitle !== filter.label) {
      await dispatch(updateFilterTypeLabel(filter._id, trimmedTitle, t));
    }

    const editResults = await Promise.all(
      state.values
        .filter((v) => v.label.trim() !== v.originalLabel)
        .map(async (v) => {
          const trimmed = v.label.trim();
          const ok = (await dispatch(editFilterValue(filter._id, v._id, trimmed, t))) as boolean;
          return { _id: v._id, newLabel: trimmed, ok };
        }),
    );

    const addResults = await Promise.all(
      state.newValues.map(async (v) => {
        const createdValue = (await dispatch(addFilterValue(filter._id, v.label.trim(), t))) as FilterValue | null;
        return { tempId: v.tempId, createdValue };
      }),
    );

    const persistedAdditions = addResults.filter((r) => r.createdValue?._id);

    setState((prev) => ({
      ...prev,
      values: [
        ...prev.values.map((v) => {
          const result = editResults.find((r) => r._id === v._id);
          return result?.ok ? { ...v, originalLabel: result.newLabel } : v;
        }),
        ...persistedAdditions.map((r) => ({
          _id: (r.createdValue as FilterValue)._id,
          label: (r.createdValue as FilterValue).label,
          originalLabel: (r.createdValue as FilterValue).label,
        })),
      ],
      newValues: prev.newValues.filter((v) => !persistedAdditions.some((r) => r.tempId === v.tempId)),
    }));

    setIsSaving(false);
  };

  const handleTitleChange = (newTitle: string): void => {
    setState((prev) => ({ ...prev, title: newTitle }));
    setErrors((prev) => ({ ...prev, title: undefined }));
  };

  const clearResolvedDuplicateErrors = (prevErrors: ErrorMap, updatedValues: ExistingValue[], updatedNewValues: NewValue[]): ErrorMap => {
    const duplicateIds = computeDuplicateIds(updatedValues, updatedNewValues);
    const duplicateMessage = t('ASSET_LIBRARY.DUPLICATE_VALUE');
    const next = { ...prevErrors };
    updatedValues.forEach((v) => {
      if (next[v._id] === duplicateMessage && !duplicateIds.has(v._id)) delete next[v._id];
    });
    updatedNewValues.forEach((v) => {
      if (next[v.tempId] === duplicateMessage && !duplicateIds.has(v.tempId)) delete next[v.tempId];
    });
    return next;
  };

  const handleValueChange = (valueId: string, newLabel: string): void => {
    const updatedValues = state.values.map((v) => (v._id === valueId ? { ...v, label: newLabel } : v));
    setState((prev) => ({ ...prev, values: updatedValues }));
    setErrors((prev) => ({
      ...clearResolvedDuplicateErrors(prev, updatedValues, state.newValues),
      [valueId]: undefined,
    }));
  };

  const handleNewValueChange = (tempId: string, newLabel: string): void => {
    const updatedNewValues = state.newValues.map((v) => (v.tempId === tempId ? { ...v, label: newLabel } : v));
    setState((prev) => ({ ...prev, newValues: updatedNewValues }));
    setErrors((prev) => ({
      ...clearResolvedDuplicateErrors(prev, state.values, updatedNewValues),
      [tempId]: undefined,
    }));
  };

  const handleDeleteExistingClick = (valueId: string): void => {
    setPendingDeleteId(valueId);
  };

  const handleDeleteConfirmed = async (): Promise<void> => {
    if (!pendingDeleteId) return;
    const deletedId = pendingDeleteId;
    setIsDeleting(true);
    try {
      const isDeleted = (await dispatch(deleteFilterValue(filter._id, deletedId, t))) as boolean;
      if (!isDeleted) return;

      const remainingValues = state.values.filter((v) => v._id !== deletedId);
      setState((prev) => ({ ...prev, values: remainingValues }));
      setErrors((prev) => {
        const next = clearResolvedDuplicateErrors(prev, remainingValues, state.newValues);
        delete next[deletedId];
        return next;
      });
    } finally {
      setIsDeleting(false);
      setPendingDeleteId(null);
    }
  };

  const handleDeleteNew = (tempId: string): void => {
    const remainingNewValues = state.newValues.filter((v) => v.tempId !== tempId);
    setState((prev) => ({ ...prev, newValues: remainingNewValues }));
    setErrors((prev) => {
      const next = clearResolvedDuplicateErrors(prev, state.values, remainingNewValues);
      delete next[tempId];
      return next;
    });
  };

  const handleAddNewRow = (): void => {
    const tempId = `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setState((prev) => ({
      ...prev,
      newValues: [...prev.newValues, { tempId, label: '' }],
    }));
  };

  const handleCloseRequest = (): void => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <BombiixSidePanel
        isOpen
        withBackdrop
        className="asset-library-filter-panel"
        title={filter.label}
        onClose={handleCloseRequest}
        saveLabel={t('ASSET_LIBRARY.SAVE')}
        saveDisabled={isSaving || !isDirty}
        {...(masterAddEditAccess ? { onSave: handleSave } : {})}
      >
        <div className="asset-library-filter-body">
          <div className="filter-title-section">
            <label className="select-label">{t('ASSET_LIBRARY.FILTER_TITLE')}</label>
            <div className="d-flex align-items-center main-filter-title">
              <div className="input-container flex-grow-1">
                <NewInput
                  value={state.title}
                  maxLength={FILTER_NAME_MAX_LENGTH}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  disabled={!masterAddEditAccess}
                  invalid={errors['title'] !== undefined}
                  aria-invalid={errors['title'] !== undefined}
                />
              </div>
            </div>
            {errors['title'] ? <label className="text-danger m-0 p-1">{errors['title']}</label> : null}
          </div>

          <ul className="filter-value-list">
            {state.values.map((v) => (
              <FilterValueRow
                key={v._id}
                value={v}
                error={errors[v._id]}
                onChange={(label: string) => handleValueChange(v._id, label)}
                onDelete={() => handleDeleteExistingClick(v._id)}
                masterAddEditAccess={masterAddEditAccess}
                masterDeleteAccess={masterDeleteAccess}
                maxLength={valueMaxLength}
              />
            ))}

            {state.newValues.map((v) => (
              <FilterValueRow
                key={v.tempId}
                value={v}
                error={errors[v.tempId]}
                onChange={(label: string) => handleNewValueChange(v.tempId, label)}
                onDelete={() => handleDeleteNew(v.tempId)}
                masterAddEditAccess={masterAddEditAccess}
                masterDeleteAccess={masterDeleteAccess}
                maxLength={valueMaxLength}
              />
            ))}
          </ul>

          {masterAddEditAccess && (
            <div className="d-flex justify-content-center add-filter-title">
              <button type="button" className="bombiix-btn btn-blue" onClick={handleAddNewRow} disabled={isSaving}>
                {`${t('ASSET_LIBRARY.ADD')} ${filter.label}`}
              </button>
            </div>
          )}
        </div>
      </BombiixSidePanel>

      {pendingDeleteId && (
        <BombiixModal show={!!pendingDeleteId} title={t('ASSET_LIBRARY.DELETE_VALUE_TITLE')} onClose={() => setPendingDeleteId(null)} hideSave size="md">
          <div className="bombiix-modal__confirm-body">
            <p className="mb-0 text-center">{t('ASSET_LIBRARY.DELETE_VALUE_CONFIRM')}</p>
            <button
              type="button"
              className="bombiix-btn btn-red"
              onClick={handleDeleteConfirmed}
              disabled={isDeleting}
              data-testid="filter-value-delete-confirm"
            >
              {t('ASSET_LIBRARY.DELETE')}
            </button>
          </div>
        </BombiixModal>
      )}

      {showCloseConfirm && (
        <BombiixModal show={showCloseConfirm} title={t('ASSET_LIBRARY.SETTINGS')} onClose={() => setShowCloseConfirm(false)} hideSave size="md">
          <div className="bombiix-modal__confirm-body">
            <p className="mb-0 text-center">{t('common:SYSTEM_MASTER.ARE_YOU_SURE_WANT_DELETE')}</p>
            <button
              type="button"
              className="bombiix-btn btn-red"
              onClick={() => {
                setShowCloseConfirm(false);
                onClose();
              }}
              data-testid="filter-panel-close-confirm"
            >
              {t('ASSET_LIBRARY.DISCARD')}
            </button>
          </div>
        </BombiixModal>
      )}
    </>
  );
}

export default FilterPanel;
